import { X, User, Phone, MessageCircle } from "lucide-react";
import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const DirectMessageModal = ({
  onClose,
  contacts,
  user,
  sendAction,
  setConversations,
  setSelectedChat,
  toast,
  api,
}) => {
  const modalRef = useRef(null);

  const [activeTab, setActiveTab] = useState("startChat");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [messageToSend, setMessageToSend] = useState("");

  const startChat = async (contact) => {
    try {
      const conversation = await api.create({
        contactId: contact.id,
        userId: user.id,
        lastLead: user.leadOrder,
      });

      setConversations((prev) => {
        const exists = prev.some((c) => c.id === conversation.id);

        if (!exists) {
          return [conversation, ...prev];
        }

        const filtered = prev.filter((c) => c.id !== conversation.id);
        return [conversation, ...filtered];
      });

      setSelectedChat(conversation);

      onClose();
    } catch (error) {
      toast.error(error);
      console.error("Erro ao atualizar status:", error);
    }
  };

  const handleSendMessage = () => {
    const cleanPhone = phoneNumber.trim();
    const cleanMessage = messageToSend.trim();

    if (!cleanPhone || !cleanMessage) {
      alert("Por favor, preencha o número e a mensagem");
      return;
    }

    sendAction("send_message", {
      userId: user.id,
      username: user.name,
      phoneNumber: cleanPhone,
      message: cleanMessage,
    });

    setMessageToSend("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleOverlayClick = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleOverlayClick}
      >
        <motion.div
          ref={modalRef}
          className="w-full max-w-2xl rounded-lg bg-white shadow-xl dark:bg-gray-900"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Cabeçalho */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between p-6">
              <h2 className="flex items-center text-xl font-semibold text-gray-900 dark:text-gray-100">
                <User className="mr-2 h-5 w-5" />
                Mensagens
              </h2>

              <button
                onClick={onClose}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex">
              <button
                onClick={() => setActiveTab("startChat")}
                className={`flex-1 px-4 py-3 text-sm font-medium ${
                  activeTab === "startChat"
                    ? "border-b-2 border-blue-500 text-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Iniciar conversa
              </button>

              <button
                onClick={() => setActiveTab("directMessage")}
                className={`flex-1 px-4 py-3 text-sm font-medium ${
                  activeTab === "directMessage"
                    ? "border-b-2 border-blue-500 text-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Mensagem direta
              </button>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="max-h-[70vh] space-y-4 overflow-y-auto p-6">
            {activeTab === "startChat" && (
              <>
                {contacts.length === 0 ? (
                  <p className="text-center text-gray-500 dark:text-gray-400">
                    Nenhum contato encontrado.
                  </p>
                ) : (
                  contacts.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {c.name}
                        </p>

                        <p className="mt-1 flex items-center text-sm text-gray-500 dark:text-gray-400">
                          <Phone className="mr-1 h-4 w-4" />
                          {c.phoneNumber.replace(/\D/g, "")}
                        </p>
                      </div>

                      <button
                        className="flex items-center gap-2 rounded-lg bg-blue-500 px-3 py-2 text-white transition hover:bg-blue-600"
                        onClick={() => startChat(c)}
                      >
                        <MessageCircle className="h-4 w-4" />
                        Iniciar Conversa
                      </button>
                    </div>
                  ))
                )}
              </>
            )}

            {activeTab === "directMessage" && (
              <div className="rounded-xl bg-white transition-colors duration-300 dark:bg-gray-900">
                <h2 className="mb-4 flex items-center text-xl font-semibold text-gray-800 dark:text-gray-100">
                  <MessageCircle className="mr-2 h-5 w-5 text-white" /> Enviar
                  Mensagem
                </h2>

                <div className="grid grid-cols-1 gap-4">
                  {/* Número */}
                  <div>
                    <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                      <Phone className="mr-2 h-4 w-4 text-green-500" />
                      Número do Telefone *
                    </label>
                    <input
                      type="text"
                      placeholder="5511999999999"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-green-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    />
                  </div>

                  {/* Mensagem */}
                  <div>
                    <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                      <MessageCircle className="mr-2 h-4 w-4 text-green-500" />
                      Mensagem *
                    </label>
                    <div className="flex gap-2">
                      <textarea
                        placeholder="Digite sua mensagem..."
                        value={messageToSend}
                        onChange={(e) => setMessageToSend(e.target.value)}
                        rows={2}
                        className="flex-1 resize-none rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-green-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                        onKeyDown={handleKeyPress}
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={!phoneNumber.trim() || !messageToSend.trim()}
                        className="rounded-lg bg-blue-500 px-6 py-2 font-semibold text-white shadow-lg transition-all hover:bg-blue-600 hover:shadow-xl disabled:cursor-not-allowed disabled:bg-gray-400 dark:disabled:bg-gray-600"
                      >
                        Enviar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DirectMessageModal;
